/**
 * Breadcrumb navigation component
 *
 * Provides hierarchical navigation context for users across applications.
 * Shows the current location within the application structure.
 *
 * Requirements: 1.6, 2.3
 */

import React from 'react';
import { ChevronRightRegular, HomeRegular } from '@fluentui/react-icons';
import styles from './Breadcrumb.module.css';

export interface BreadcrumbItem {
  /** Display text for the breadcrumb item */
  label: string;
  /** URL to navigate to when clicked (optional for current page) */
  href?: string;
  /** Icon to display before the label (optional) */
  icon?: React.ComponentType<{ className?: string }>;
}

export interface BreadcrumbProps {
  /** Array of breadcrumb items from root to current page */
  items: BreadcrumbItem[];
  /** Maximum number of items to show before collapsing */
  maxItems?: number;
  /** Custom separator between items */
  separator?: React.ReactNode;
}

/**
 * Generates breadcrumb items for common application paths
 * Pure function for testability
 *
 * @param currentApp - Current application identifier
 * @param currentPage - Current page within the app (optional)
 * @returns Array of breadcrumb items
 */
export function generateBreadcrumbItems(
  currentApp: string,
  currentPage?: string
): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: HomeRegular,
    },
  ];

  // Add application-specific breadcrumbs
  switch (currentApp) {
    case 'admin':
      items.push({
        label: 'Administration',
        href: currentPage ? '/admin' : undefined,
      });
      if (currentPage) {
        items.push({
          label: currentPage,
        });
      }
      break;

    case 'policy_navigator':
      items.push({
        label: 'Policy Navigator',
        href: currentPage ? '/policy-navigator' : undefined,
      });
      if (currentPage) {
        items.push({
          label: currentPage,
        });
      }
      break;

    case 'evidence_extractor':
      items.push({
        label: 'Evidence Extractor',
        href: currentPage ? '/evidence-extractor' : undefined,
      });
      if (currentPage) {
        items.push({
          label: currentPage,
        });
      }
      break;

    default:
      // For landing page, just show dashboard
      break;
  }

  return items;
}

/**
 * Breadcrumb navigation component
 *
 * Features:
 * - Hierarchical navigation display (Req 1.6)
 * - Clickable navigation links (Req 2.3)
 * - Accessible navigation structure
 * - Responsive design with item collapsing
 * - Customizable separators and icons
 */
export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  maxItems = 5,
  separator = <ChevronRightRegular className={styles.separator} />,
}) => {
  // Handle item collapsing if there are too many items
  const displayItems = items.length > maxItems
    ? [
        items[0], // Always show first item (home)
        { label: '...', href: undefined }, // Ellipsis
        ...items.slice(-(maxItems - 2)), // Show last few items
      ]
    : items;

  return (
    <nav className={styles.breadcrumb} aria-label="Breadcrumb navigation">
      <ol className={styles.breadcrumbList}>
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          const isEllipsis = item.label === '...';
          const Icon = item.icon;

          return (
            <li key={`${item.label}-${index}`} className={styles.breadcrumbItem}>
              {isEllipsis ? (
                <span className={styles.ellipsis} aria-hidden="true">
                  {item.label}
                </span>
              ) : item.href && !isLast ? (
                <a
                  href={item.href}
                  className={styles.breadcrumbLink}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {Icon && <Icon className={styles.icon} />}
                  <span>{item.label}</span>
                </a>
              ) : (
                <span
                  className={`${styles.breadcrumbText} ${isLast ? styles.current : ''}`}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {Icon && <Icon className={styles.icon} />}
                  <span>{item.label}</span>
                </span>
              )}
              {!isLast && (
                <span className={styles.separatorWrapper} aria-hidden="true">
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
